import {
  DataTableSortableColumn,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  TooltipTrigger,
  TooltipContent,
  DropdownMenu,
  Typography,
  ColumnDef,
  Checkbox,
  Tooltip,
  Button,
  Badge,
} from '@ui/components';
import { VehicleTableDataDto, ServiceType } from '@interfaces/models';
import { MenuVertical } from '@ui/components/icons';
import { HasPermission } from '@shared/components';
import { ServiceIcons } from '@shared/utils';
import { TFunction } from 'i18next';

export const getVehiclesColumns = (
  t: TFunction,
  onEditVehicle: (vehicle: VehicleTableDataDto) => void,
  onRemoveVehicle: (vehicle: VehicleTableDataDto) => void,
): ColumnDef<VehicleTableDataDto>[] => [
  {
    id: 'select',
    size: 40,
    maxSize: 40,
    header: ({ table }) => (
      <Checkbox
        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        checked={table.getIsAllRowsSelected()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        checked={row.getIsSelected()}
        className="ml-0.5"
      />
    ),
  },
  {
    accessorKey: 'registrationNumber',
    maxSize: 60,
    size: 168,
    header: ({ column }) => (
      <DataTableSortableColumn label={t('VEHICLES.TABLE_HEADER_PLATE_NUMBER')} column={column} />
    ),
  },
  {
    accessorKey: 'name',
    maxSize: 300,
    header: ({ column }) => (
      <DataTableSortableColumn label={t('VEHICLES.TABLE_HEADER_NAME')} column={column} />
    ),
  },
  {
    accessorKey: 'costCenters',
    maxSize: 300,
    header: t('VEHICLES.TABLE_HEADER_COST_CENTER'),
    cell: ({ row }) =>
      row.original.costCenters?.map((costCenter) => costCenter.name).join(', ') ?? '-',
  },
  {
    accessorKey: 'services',
    header: t('VEHICLES.TABLE_HEADER_SERVICES'),
    cell: ({ row }) => {
      const services = row.original.services ?? [];
      const serviceCount = services.length;

      if (!serviceCount) {
        return '-';
      }

      return (
        <div className="flex items-center gap-1 whitespace-nowrap">
          <Badge size="large" outlined>
            {ServiceIcons[services[0] as ServiceType]}
            {t(`SERVICE_TYPE.${services[0]}`)}
          </Badge>
          {serviceCount > 1 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge size="large" outlined icon>
                  +{serviceCount - 1}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="bg-background flex gap-2 w-auto max-w-[15rem] flex-wrap">
                {services.slice(1).map((service) => (
                  <Badge key={service} size="large" outlined>
                    {ServiceIcons[service as ServiceType]}
                    {t(`SERVICE_TYPE.${service}`)}
                  </Badge>
                ))}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'registrationCountry',
    header: ({ column }) => (
      <DataTableSortableColumn label={t('VEHICLES.TABLE_HEADER_COUNTRY_CODE')} column={column} />
    ),
  },
  {
    accessorKey: 'vehicleCategory',
    maxSize: 300,
    header: ({ column }) => (
      <DataTableSortableColumn label={t('VEHICLES.TABLE_HEADER_CATEGORY')} column={column} />
    ),
    cell: ({ row }) => {
      return t(`VEHICLE_CATEGORY.${row.original.vehicleCategory}`);
    },
  },
  {
    accessorKey: 'actions',
    header: '',
    size: 60,
    maxSize: 60,
    cell: ({ row }) => {
      const rowData = row.original;

      return (
        <div className="flex justify-end">
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger tabIndex={-1} asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost">
                    <MenuVertical />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent className="w-auto">
                <Typography variant="labelXSmallBook">
                  {t('VEHICLES.TABLE_MORE_ACTIONS')}
                </Typography>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent className="w-56">
              <HasPermission permissionFeature="VEHICLES_EDIT_VEHICLE" permissionType="WRITE">
                <DropdownMenuItem onClick={() => onEditVehicle(rowData)}>
                  {t('VEHICLES.TABLE_ASSIGN_TO_EMPLOYEE')}
                </DropdownMenuItem>
              </HasPermission>
              <HasPermission permissionFeature="VEHICLES_EDIT_VEHICLE" permissionType="READ">
                <DropdownMenuItem onClick={() => onEditVehicle(rowData)}>
                  {t('VEHICLES.TABLE_EDIT_VEHICLE')}
                </DropdownMenuItem>
              </HasPermission>
              <HasPermission permissionFeature="VEHICLES_REMOVE_VEHICLE" permissionType="WRITE">
                <DropdownMenuItem onClick={() => onRemoveVehicle(rowData)}>
                  {t('VEHICLES.TABLE_DELETE_VEHICLE')}
                </DropdownMenuItem>
              </HasPermission>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  },
];
